import { Form, Input, FormProps, InputNumber } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import FRadioGroup from 'components/FRadioGroup';
import { VoucherType, RewardType, VoucherDisplayPosition } from 'services/api/voucher';
import Checkbox from 'antd/es/checkbox/Checkbox';
import styled from '@emotion/styled';
import { regionMonetaryUnitMap, upperCaseRegionRegionMap } from 'services/config/region';
import dayjs, { Dayjs } from 'lib/dayjs';
import Tooltip from 'antd/es/tooltip';
import { QuestionCircleOutlined } from '@ant-design/icons';
import Button from 'antd/es/button';
import { css } from '@emotion/css';
import { inputPosInt, inputPosDecimal, limitPrecision } from 'utils/inputCheck';
import FormWrap from './styled/FormWrap';
import AddProduct from '../ProductSelector/AddProduct';
import { generateVoucherCode, hasValue } from './utils';
import useVoucherValues from './useVoucherValues';
import CacheTips from './CacheTips';
import { DatePicker } from 'components/DatePicker';
import { getCouponTypeLabel } from 'services/i18n/enum/coupon';

const { RangePicker } = DatePicker;

const CheckBoxWrap = styled.div`
  margin-bottom: 8px;
`;
const CodeLabel = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;

  .ant-btn-link {
    padding-left: 0;
    padding-right: 0;
  }
`;
const codeFormItem = css`
  label {
    width: 100%;
  }
  .ant-form-item-label:after {
    display: none;
  }
`;

export interface ShopeeVoucherFormProps extends FormProps {
  storeName?: string;
  storeId: string;
  region: General.UpperCaseRegion;
  status?: Conversation.Voucher.Status;
  disableCache?: boolean;
}

// 最高折扣限制
const MAX_DISCOUNT = 99;
const FormRegionConfig: Record<
  string,
  {
    /** 金额递增步长 */
    step: number;
    /** 折扣金额 最小 */
    minDiscountAmount: number;
    /** 折扣金额 最大 */
    maxDiscountAmount: number;
    /** 折扣百分比 最小 */
    minDiscount: number;
    /** 支持shoppe币回扣 */
    shoppeCoinSupport: boolean;
    /** 进行中优惠券显示设置可修改 */
    modifyDisplaySettingOnWay: boolean;
  }
> = {
  TH: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: false,
  },
  ID: {
    step: 1,
    minDiscountAmount: 99,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  BR: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000.0,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  FR: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 10,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  PL: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  ES: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  TW: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  MY: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000.0,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  PH: {
    step: 1,
    minDiscountAmount: 5,
    maxDiscountAmount: 1000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  SG: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000.0,
    minDiscount: 3,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  VN: {
    step: 1,
    minDiscountAmount: 1000,
    maxDiscountAmount: 120000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  CL: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  CO: {
    step: 1,
    minDiscountAmount: 1,
    maxDiscountAmount: 1000000000,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
  MX: {
    step: 0.01,
    minDiscountAmount: 0.1,
    maxDiscountAmount: 1000000000.0,
    minDiscount: 1,
    shoppeCoinSupport: true,
    modifyDisplaySettingOnWay: true,
  },
};

const isDisabled = (fieldName: string, status?: Conversation.Voucher.Status) => {
  const formDisabledStatus: { [key: string]: Conversation.Voucher.Status[] } = {
    voucherType: ['SCHEDULED', 'ONGOING'],
    voucherName: [],
    times: ['ONGOING'],
    displayCollectTime: ['SCHEDULED', 'ONGOING'],
    voucherCode: ['SCHEDULED', 'ONGOING'],
    rewardType: ['SCHEDULED', 'ONGOING'],
    percentage: ['ONGOING'],
    discountAmount: ['ONGOING'],
    maxPrice: ['ONGOING'],
    minBasketPrice: ['ONGOING'],
    itemIdList: [],
  };

  if (Array.isArray(formDisabledStatus[fieldName]) && status) {
    return formDisabledStatus[fieldName].includes(status);
  }
  return false;
};

const ShopeeVoucherForm = (props: ShopeeVoucherFormProps) => {
  const initStartTime = dayjs().add(2, 'minutes');
  const initEndTime = dayjs(initStartTime).add(1, 'day');

  const {
    status,
    storeName,
    storeId,
    region = 'ID',
    initialValues = {
      voucherType: VoucherType.Shop,
      rewardType: RewardType.FixAmount,
      times: [initStartTime, initEndTime],
      voucherCode: generateVoucherCode(),
      itemIdList: [],
    },
    form,
    disableCache,
    ...rest
  } = props;
  const { t } = useTranslate();
  const { clearVoucherFormCache, setVoucherFormCache, getVoucherFormCache } = useVoucherValues();
  const [maxPriceShow, setMaxPriceShow] = useState<boolean>(
    initialValues.rewardType !== RewardType.FixAmount,
  );

  const [type, setType] = useState(VoucherType.Shop);
  const [discount, setDiscount] = useState(RewardType.FixAmount);
  const [displaySettings, setDisplaySettings] = useState<number | undefined>();
  const [showCacheTips, setShowCacheTips] = useState(false);

  const [isLookAhead, setIsLookAhead] = useState<boolean>(false);

  const { [region]: lowRegion } = upperCaseRegionRegionMap;
  const { [lowRegion]: moneyUnit } = regionMonetaryUnitMap;
  const formRegionConfig = FormRegionConfig[region];

  const typeOptions = useMemo(() => {
    return [
      {
        label: getCouponTypeLabel(t, VoucherType.Shop),
        value: VoucherType.Shop,
      },
      {
        label: getCouponTypeLabel(t, VoucherType.Product),
        value: VoucherType.Product,
      },
    ];
  }, [t]);

  const discountTypeOptions = useMemo(() => {
    const base: Array<{
      label: string;
      value: RewardType;
      extra?: React.ReactNode;
    }> = [
      {
        label: t('coupon.discountType.discountPercentage'),
        value: RewardType.DiscountPercentage,
      },
      {
        label: t('coupon.discountType.discountAmount'),
        value: RewardType.FixAmount,
      },
    ];

    if (formRegionConfig.shoppeCoinSupport) {
      base.push({
        label: t('coupon.discountType.shopeeCoins'),
        value: RewardType.CoinCashback,
        extra: (
          <Tooltip title={t('chatWorkspace.couponForm.tooltip.shopeeCoins')}>
            <QuestionCircleOutlined style={{ marginLeft: 4 }} />
          </Tooltip>
        ),
      });
    }
    return base;
  }, [formRegionConfig.shoppeCoinSupport, t]);

  const displaySettingsOptions = useMemo(() => {
    return [
      {
        label: t('coupon.displaySettings.displayAllPages'),
        value: VoucherDisplayPosition.AllPage,
      },
      {
        label: t('coupon.displaySettings.distributeViaCode'),
        value: undefined,
      },
    ];
  }, [t]);

  // 显示设置禁用状态
  const displaySettingsDisabled = useMemo(() => {
    if (isLookAhead) {
      // 优惠券提前查看, 不可编辑
      return true;
    }

    if (status === 'ONGOING') {
      return !formRegionConfig.modifyDisplaySettingOnWay;
    }
    if (status === 'SCHEDULED') {
      return true;
    }
    return false;
  }, [formRegionConfig.modifyDisplaySettingOnWay, status, isLookAhead]);

  useEffect(() => {
    if (!disableCache) {
      // 从缓存读取
      const cached = getVoucherFormCache(storeId);
      if (cached && !status) {
        form?.setFieldsValue(cached);
        setShowCacheTips(true);
      }
    }

    const displayChannel = form?.getFieldValue('displayChannel');
    if (displayChannel) {
      setDisplaySettings(displayChannel);
    }
    const voucherType = form?.getFieldValue('voucherType');
    if (voucherType) {
      setType(form?.getFieldValue('voucherType'));
    }
    const rewardType = form?.getFieldValue('rewardType');
    if (rewardType) {
      setDiscount(rewardType);
      setMaxPriceShow(rewardType !== RewardType.FixAmount);
    }
    const displayStartTime = form?.getFieldValue('displayStartTime');
    if (displayStartTime && displayStartTime !== '0') {
      setIsLookAhead(true);
    } else {
      setIsLookAhead(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableCache, form]);

  // 是否是创建表单状态
  const isCreateStatus = useMemo(() => !status, [status]);

  return (
    <Form
      {...rest}
      form={form}
      layout="vertical"
      initialValues={initialValues}
      scrollToFirstError
      onValuesChange={(_, allValues) => {
        if (isCreateStatus) {
          // 新增状态下缓存
          setVoucherFormCache(storeId, allValues);
        }
      }}
    >
      {!disableCache && (
        <CacheTips
          visible={showCacheTips}
          onClick={() => {
            clearVoucherFormCache(storeId);
            form?.resetFields();
            setShowCacheTips(false);
          }}
        />
      )}
      {storeName ? (
        <FormWrap>
          <Form.Item label={t('coupon.field.shopName')} style={{ marginBottom: 0 }}>
            {storeName}
          </Form.Item>
        </FormWrap>
      ) : null}

      <FormWrap title={t('chatWorkspace.couponForm.section.basicInfo')}>
        <Form.Item label={t('coupon.field.type')} name="voucherType" required>
          <FRadioGroup
            options={typeOptions}
            value={type}
            disabled={isDisabled('voucherType', status)}
            onChange={(v) => {
              setType(v as number);
              form?.setFieldsValue({
                voucherType: v,
              });

              if (v === VoucherType.Shop) {
                form?.setFieldsValue({
                  itemIdList: [],
                });
              }
            }}
          />
        </Form.Item>
        <Form.Item
          label={t('coupon.field.name')}
          name="voucherName"
          rules={[{ required: true, message: t('common.validation.required') }]}
          required
        >
          <Input
            placeholder={t('chatWorkspace.couponForm.placeholder.couponName')}
            maxLength={100}
            disabled={isDisabled('voucherName', status)}
          />
        </Form.Item>
        <Form.Item
          label={t('coupon.field.claimPeriod')}
          name="times"
          required
          rules={[
            {
              required: true,
              message: t('common.validation.required'),
              type: 'array',
            },
            {
              validator: (_, value: [Dayjs, Dayjs]) => {
                if (!isDisabled('times', status) && value) {
                  const [startTime, endTime] = value;
                  if (startTime.isBefore(Date.now())) {
                    return Promise.reject(
                      new Error(t('chatWorkspace.couponForm.validation.startTimeInPast')),
                    );
                  }
                  if (endTime.diff(startTime, 'days') > 90) {
                    return Promise.reject(
                      new Error(
                        t('chatWorkspace.couponForm.validation.durationExceedsMax', { months: 3 }),
                      ),
                    );
                  }
                  if (endTime.diff(startTime, 'hours') < 1) {
                    return Promise.reject(
                      new Error(
                        t('chatWorkspace.couponForm.validation.durationTooShortHours', {
                          hours: 1,
                        }),
                      ),
                    );
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <RangePicker
            showTime
            disabled={[isDisabled('times', status), false]}
            disabledDate={(current) => {
              return current.isBefore(dayjs(Date.now()));
            }}
          />
        </Form.Item>
        <CheckBoxWrap>
          <Checkbox
            checked={isLookAhead}
            disabled={isDisabled('displayCollectTime', status)}
            onChange={(e) => {
              const { checked } = e.target;
              if (checked) {
                const [startTime] = form?.getFieldValue('times') as [Dayjs, Dayjs];
                form?.setFieldsValue({
                  displayCollectTime: dayjs(startTime).subtract(10, 'minutes'),
                  displayChannel: VoucherDisplayPosition.AllPage,
                });
                // 优惠券提前查看, 显示设置为所有页面且不可编辑
                setDisplaySettings(VoucherDisplayPosition.AllPage);
              } else {
                form?.setFieldsValue({
                  displayCollectTime: undefined,
                });
              }
              setIsLookAhead(checked);
            }}
          >
            {t('coupon.field.enablePreview')}
          </Checkbox>
        </CheckBoxWrap>
        {isLookAhead ? (
          <Form.Item name="displayCollectTime">
            <DatePicker
              showTime
              disabled={isDisabled('displayCollectTime', status)}
              clearIcon={false}
              disabledDate={(current) => {
                const [startTime] = form?.getFieldValue('times');
                return current.isAfter(startTime);
              }}
            />
          </Form.Item>
        ) : null}
        <Form.Item
          className={codeFormItem}
          label={
            <CodeLabel>
              <span>{t('coupon.field.code')}:</span>
              {isDisabled('voucherCode', status) ? null : (
                <Button
                  type="link"
                  onClick={() => {
                    const code = generateVoucherCode();
                    form?.setFieldsValue({
                      voucherCode: code,
                    });
                  }}
                >
                  {t('chatWorkspace.couponForm.button.regenerate')}
                </Button>
              )}
            </CodeLabel>
          }
          name="voucherCode"
          required
          rules={[
            { required: true, message: t('common.validation.required') },
            {
              validator: (_, value) => {
                if (hasValue(value) && !/^[A-Z0-9]+$/.test(value)) {
                  return Promise.reject(
                    new Error(t('chatWorkspace.couponForm.validation.codeFormat')),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Input
            showCount
            maxLength={5}
            placeholder={t('chatWorkspace.couponForm.placeholder.couponCode')}
            disabled={isDisabled('voucherCode', status)}
            onChange={(e) => {
              const { value } = e.target;
              if (/^[A-Z0-9]+$/.test(value)) {
                form?.setFieldsValue({
                  voucherCode: value.toUpperCase(),
                });
              }
            }}
            onBlur={(e) => {
              const { value } = e.target;
              if (value) {
                form?.setFieldsValue({
                  voucherCode: value.toUpperCase(),
                });
              }
            }}
          />
        </Form.Item>
      </FormWrap>

      <FormWrap title={t('chatWorkspace.couponForm.section.discountSettings')}>
        <Form.Item label={t('coupon.field.discountType')} name="rewardType" required>
          <FRadioGroup
            value={discount}
            options={discountTypeOptions}
            disabled={isDisabled('rewardType', status)}
            onChange={(v) => {
              setDiscount(v as number);
              setMaxPriceShow(v !== RewardType.FixAmount);
              form?.setFieldsValue({
                rewardType: v,
              });
              setTimeout(
                () =>
                  form?.validateFields([
                    'percentage',
                    'discountAmount',
                    'maxPrice',
                    'minBasketPrice',
                  ]),
                0,
              );
            }}
          />
        </Form.Item>

        {maxPriceShow ? (
          <Form.Item
            label={t('coupon.field.discountPercentage')}
            name="percentage"
            required
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (!isDisabled('percentage', status) && hasValue(value)) {
                    if (value < formRegionConfig.minDiscount) {
                      return Promise.reject(
                        new Error(
                          t('chatWorkspace.couponForm.validation.discountPercentageMin', {
                            discountPercentage: formRegionConfig.minDiscount,
                          }),
                        ),
                      );
                    }
                    if (value > MAX_DISCOUNT) {
                      return Promise.reject(
                        new Error(
                          t('chatWorkspace.couponForm.validation.discountPercentageMax', {
                            discountPercentage: MAX_DISCOUNT,
                          }),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              disabled={isDisabled('percentage', status)}
              addonAfter={
                discount === RewardType.CoinCashback
                  ? `%${t('coupon.discountType.shopeeCoins')}`
                  : '%'
              }
              placeholder={t('common.placeholder')}
              min={formRegionConfig.minDiscount}
              max={MAX_DISCOUNT}
              precision={0}
              step={1}
              onKeyPress={inputPosInt}
            />
          </Form.Item>
        ) : (
          <Form.Item
            label={t('coupon.field.discountAmount')}
            name="discountAmount"
            required
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ discountAmount: v.precisionVal });
                    }
                  }
                  if (!isDisabled('discountAmount', status)) {
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    // @tutu @靳艳华 确认取消该限制
                    // if (region === 'TH') {
                    //   // 不高于最低消80%
                    //   if (hasValue(minBasketPrice) && value > minBasketPrice * 0.8) {
                    //     return Promise.reject(
                    //       new Error(
                    //         t('formValidator.rule6', {
                    //           discountPercentage: 80,
                    //         }),
                    //       ),
                    //     );
                    //   }
                    // }
                    // if (region === 'MY' || region === 'ID') {
                    //   // 不高于最低消50%
                    //   if (hasValue(minBasketPrice) && value > minBasketPrice * 0.5) {
                    //     return Promise.reject(
                    //       new Error(
                    //         t('formValidator.rule6', {
                    //           discountPercentage: 50,
                    //         }),
                    //       ),
                    //     );
                    //   }
                    // }
                    // @tutu @靳艳华 确认最底消费金额为0则认为该配置为无门槛券，可以跳过限制
                    if (minBasketPrice === 0) {
                      return Promise.resolve();
                    }
                    if (hasValue(minBasketPrice) && value > minBasketPrice) {
                      return Promise.reject(
                        new Error(t('chatWorkspace.couponForm.validation.discountExceedsMinSpend')),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              addonAfter={moneyUnit}
              disabled={isDisabled('discountAmount', status)}
              placeholder={t('common.placeholder')}
              min={formRegionConfig.minDiscountAmount}
              max={formRegionConfig.maxDiscountAmount}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        )}
        {maxPriceShow ? (
          <Form.Item
            label={t('coupon.field.maxDiscount')}
            name="maxPrice"
            dependencies={['minBasketPrice', 'percentage']}
            required
            rules={[
              { required: true, message: t('common.validation.required') },
              {
                validator: (_, value) => {
                  if (formRegionConfig.step < 1) {
                    const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                    if (!v.precisionMatch) {
                      form?.setFieldsValue({ maxPrice: v.precisionVal });
                    }
                  }
                  if (!isDisabled('maxPrice', status) && value) {
                    const minBasketPrice = form?.getFieldValue('minBasketPrice');
                    // @tutu @靳艳华 确认最底消费金额为0则认为该配置为无门槛券，可以跳过限制
                    if (minBasketPrice === 0) {
                      return Promise.resolve();
                    }

                    const percentage = form?.getFieldValue('percentage');

                    if (
                      !hasValue(percentage) &&
                      hasValue(minBasketPrice) &&
                      value > minBasketPrice
                    ) {
                      return Promise.reject(
                        new Error(
                          t('chatWorkspace.couponForm.validation.maxDiscountLessThanMinSpend'),
                        ),
                      );
                    }

                    if (
                      hasValue(percentage) &&
                      hasValue(minBasketPrice) &&
                      value < (minBasketPrice * percentage) / 100
                    ) {
                      return Promise.reject(
                        new Error(
                          t('chatWorkspace.couponForm.validation.maxDiscountLessThanCalculated', {
                            discountPercentage: percentage,
                          }),
                        ),
                      );
                    }
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder={t('common.placeholder')}
              addonAfter={moneyUnit}
              disabled={isDisabled('maxPrice', status)}
              min={formRegionConfig.minDiscountAmount}
              max={formRegionConfig.maxDiscountAmount}
              precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
              step={formRegionConfig.step}
              onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
            />
          </Form.Item>
        ) : null}

        <Form.Item
          label={t('coupon.field.minSpend')}
          name="minBasketPrice"
          required
          rules={[
            { required: true, message: t('common.validation.required') },
            {
              validator: (_, value) => {
                if (formRegionConfig.step < 1) {
                  const v = limitPrecision(value, Math.abs(Math.log10(formRegionConfig.step)));
                  if (!v.precisionMatch) {
                    form?.setFieldsValue({ minBasketPrice: v.precisionVal });
                  }
                }
                const maxPrice = form?.getFieldValue('maxPrice');
                if (
                  !isDisabled('minBasketPrice', status) &&
                  hasValue(value) &&
                  hasValue(maxPrice) &&
                  maxPrice > value
                ) {
                  // @tutu @靳艳华 确认最底消费金额为0则认为该配置为无门槛券，可以跳过限制
                  if (value === 0) {
                    return Promise.resolve();
                  }

                  const percentage = form?.getFieldValue('percentage');
                  if (hasValue(percentage)) {
                    return Promise.resolve();
                  }

                  return Promise.reject(
                    new Error(t('chatWorkspace.couponForm.validation.minSpendLessThanMaxDiscount')),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber
            placeholder={t('common.placeholder')}
            addonAfter={moneyUnit}
            disabled={isDisabled('minBasketPrice', status)}
            min={0}
            max={formRegionConfig.maxDiscountAmount}
            precision={formRegionConfig.step ? Math.abs(Math.log10(formRegionConfig.step)) : 0}
            step={formRegionConfig.step}
            onBlur={() => {
              const value = form?.getFieldValue('minBasketPrice');
              if (
                formRegionConfig.minDiscountAmount &&
                value > 0 &&
                value < formRegionConfig.minDiscountAmount
              ) {
                form?.setFieldsValue({ minBasketPrice: formRegionConfig.minDiscountAmount });
              }
              form?.validateFields(['discountAmount']);
            }}
            onChange={() => {
              form?.validateFields(['discountAmount']);
            }}
            onKeyPress={formRegionConfig.step === 1 ? inputPosInt : inputPosDecimal}
          />
        </Form.Item>
        <Form.Item
          label={t('coupon.field.totalQuantity')}
          name="usageQuantity"
          required
          rules={[{ required: true, message: t('common.validation.required') }]}
        >
          <InputNumber
            style={{ width: '100%' }}
            placeholder={t('common.placeholder')}
            min={1}
            max={200000}
            precision={0}
            step={1}
            onKeyPress={inputPosInt}
          />
        </Form.Item>
      </FormWrap>

      <FormWrap title={t('chatWorkspace.couponForm.section.otherSettings')}>
        <Form.Item label={t('coupon.field.displaySettings')} name="displayChannel" required>
          <FRadioGroup
            value={displaySettings}
            options={displaySettingsOptions}
            disabled={displaySettingsDisabled}
            onChange={(v) => {
              setDisplaySettings(v as number);
              if (v === -1) {
                form?.setFieldsValue({
                  displayChannel: undefined,
                });
              } else {
                form?.setFieldsValue({
                  displayChannel: v,
                });
              }
            }}
          />
        </Form.Item>
        {storeId && type === VoucherType.Product ? (
          <Form.Item
            label={t('coupon.field.eligibleProducts')}
            name="itemIdList"
            required
            rules={[
              {
                required: true,
                message: t('common.validation.requiredSelect'),
                type: 'array',
              },
            ]}
          >
            <AddProduct storeId={storeId} disabled={isDisabled('itemIdList', status)} />
          </Form.Item>
        ) : null}
      </FormWrap>
    </Form>
  );
};

export default ShopeeVoucherForm;
